import React, { useState, useEffect } from "react";
import axios from "axios";
import Calendar from "react-calendar";
import "./Calendar.scss";
import dayjs from "../dayjs";
import { bookingTimestamp, dayStartISO } from "../bookingTime";
import Loader from "./Loader";
import { get24Hrs } from "./Dialog";
import isEmail from "validator/es/lib/isEmail";
import { useAppData } from "../Context/AppContext";

import { PhoneInput as ReactPhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';


const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TIME_API_URL = process.env.REACT_APP_API_URL || "https://run.salesblink.io/api/meet/slug";
const SLUG = window.location.pathname?.split("/").filter(Boolean).pop() || window.location.hostname;

function buildTimeUrl(path) {
    return `${TIME_API_URL}/${SLUG}/${path}`;
}

function TimePicker({ selectedDate, onSelect, userDetails, timeZone, showTimeIn24Hr, setShowTimeIn24Hr, selectedTime: externalSelectedTime }) {
    const [times, setTimes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTime, setSelectedTime] = useState(externalSelectedTime || null);
    const appData = useAppData();

    useEffect(() => {
        setSelectedTime(externalSelectedTime || null);
    }, [externalSelectedTime]);

    async function fetchTimes() {
        try {
            const isoDate = dayStartISO(selectedDate, timeZone);
            let { data } = await axios.post(buildTimeUrl("times"), { selectedDate: isoDate, timeZone });
            setTimes(data.data);
            setIsLoading(false);
        } catch (e) {
            console.log("Error in fetching time from API ", e.response || e.message);
            setIsLoading(false);
            if(e?.response?.data?.title) appData?.setError(e?.response?.data?.title)
            if(e?.response?.data?.description) appData?.setErrorDescription(e?.response?.data?.description)
        }
    }

    function toggleShowTimeIn24Hr() {
        setShowTimeIn24Hr(!showTimeIn24Hr);
    }

    function handleTimeSelect(time) {
        setSelectedTime(time);
        // Brief delay so the user sees the selected state before the form appears
        setTimeout(() => {
            onSelect(time);
        }, 150);
    }

    useEffect(() => {
        if (selectedDate) {
            setIsLoading(true);
            fetchTimes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, timeZone]);

    return (
        <div className="time-picker">
            {isLoading ? (
                <div style={{ margin: "auto" }}>
                    <Loader style={{ width: 40, height: 40, borderWidth: 4 }} />
                </div>
            ) : (
                <>
                    <div className="header">
                        <div className="date">
                            <div className="day">{DAYS[selectedDate.getDay()]}</div>
                            <div className="date-month">{`${selectedDate.getDate()} ${MONTHS[selectedDate.getMonth()]}`}</div>
                        </div>
                        <div className="switch" onClick={toggleShowTimeIn24Hr}>
                            <span className={`_12hr ${showTimeIn24Hr ? "" : "enabled"}`}>12H</span>
                            <span className={`_24hr ${showTimeIn24Hr ? "enabled" : ""}`}>24H</span>
                        </div>
                    </div>
                    <div className="slots">
                        {times.length === 0 && <h4 style={{ fontWeight: 500, textAlign: "center" }}>Oops, No Time Slots found!!</h4>}
                        {times.length > 0 &&
                            times.map((time) => (
                                <button
                                    onClick={() => handleTimeSelect(time)}
                                    className={`slot ${selectedTime === time ? "selected" : ""}`}
                                    key={time}
                                    type="button"
                                >
                                    {showTimeIn24Hr ? get24Hrs(time) : time}
                                </button>
                            ))}
                    </div>
                </>
            )}
        </div>
    );
}

function ScheduleForm({ customFields, onCancel, selectedTime, timeZone, date, setMeetingBooked }) {
    const [customQuestions, setCustomQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const appData = useAppData();

    const getFieldError = (question, value) => {
        const answer = value ?? question.answer;
        if (question.required === "required" && (!answer || answer.toString().trim() === "")) {
            return `${question.question || 'This field'} is required`;
        }
        // i-2 is the built-in email question; it is a short_text field, so check it explicitly
        if ((question.type === "email" || question.field === "email" || question.id === "i-2") && answer && !isEmail(answer.toString().trim())) {
            return 'Invalid email';
        }
        if (question.type === "number" && answer !== "" && answer !== undefined && Number.isNaN(Number(answer))) {
            return 'Invalid number';
        }
        if (question.type === "number" && question.required === "required" && Number(answer) < 0) {
            return 'Must be a positive number';
        }
        return "";
    };

    const scheduleEvent = async () => {
        const nextErrors = {};
        let hasError = false;

        for (const customQuestion of customQuestions) {
            const error = getFieldError(customQuestion);
            if (error) {
                nextErrors[customQuestion.id] = error;
                hasError = true;
            }
        }

        setErrors(nextErrors);
        if (hasError) return;

        setLoading(true);
        try {
            // let time = `${moment(date).format("YYYY-MM-DD")}T${moment(selectedTime, "H:mm a").format("HH:mm:ss")}`;
            // time = new Date(time).getTime();
            // the clicked day and slot time, in the timezone the visitor selected
            const time = bookingTimestamp(date, selectedTime, timeZone);

            let { data } = await axios.post(buildTimeUrl("create"), {
                customQuestions,
                time,
                timeZone
            });

            setLoading(false);

            // iframe detection
            if (window.self !== window.top) {
                if (data.data === "https://salesblink.io/") return setMeetingBooked(true);
                window.top.location.href = data.data;
                return;
            }

            if (data.data === "https://salesblink.io/") return setMeetingBooked(true);
            window.location.href = data.data;
        } catch (e) {
            setLoading(false);
            console.log("Error in fetching time from API ", e.response);
            if(e?.response?.data?.title) appData?.setError(e?.response?.data?.title)
            if(e?.response?.data?.description) appData?.setErrorDescription(e?.response?.data?.description)
        }
    };
    const updateAnswer = (id, answer) => {
        setCustomQuestions((oldFields) => {
            const newFields = oldFields.map((field) => {
                if (field.id === id) {
                    const next = { ...field, answer };
                    const error = getFieldError(next, answer);
                    setErrors((prev) => ({
                        ...prev,
                        [id]: error
                    }));
                    return next;
                }
                return field;
            });
            return newFields;
        });
    };


    useEffect(() => {
        if (customFields && customFields.length > 0) {
            let customQuestionArr = [];
            for (let i = 0; i < customFields.length; i++) {
                customQuestionArr.push({
                    id: customFields[i].id,
                    question: customFields[i].question,
                    required: customFields[i].required,
                    field: customFields[i].field,
                    answer: "",
                    type: customFields[i].type
                });
            }
            // console.log("QUES ",customQuestionArr)
            setCustomQuestions(customQuestionArr);
        }
    }, [customFields]);

    return (
        <div className="schedule-form">
            <form className="form">
                <h1 className="header">Enter Details</h1>
                {customQuestions.map((customQuestion) => (
                    <div key={customQuestion.id} className="input-container">
                        <label htmlFor={customQuestion.id} aria-required={customQuestion.required === "required"}>
                            {customQuestion.question}
                        </label>
                        {(function () {
                            switch (customQuestion.type) {
                                case "long_text":
                                    return (
                                        <textarea
                                            id={customQuestion.id}
                                            className=""
                                            name="name"
                                            value={customQuestion.answer}
                                            onChange={(e) => updateAnswer(customQuestion.id, e.target.value)}
                                            required={customQuestion.required === "required"}
                                            aria-invalid={!!errors[customQuestion.id]}
                                        />
                                    );
                                case "phone_number":
                                    return (
                                        <ReactPhoneInput
                                            defaultCountry="us"
                                            value={customQuestion.answer}
                                            onChange={(number) => updateAnswer(customQuestion.id, number)}
                                            style={{ height: 50 }}
                                            countrySelectorStyleProps={{ buttonStyle: { marginTop: "unset", height: "40px" } }}
                                            inputStyle={{
                                                width: "286px",
                                                height: "40px",
                                                borderRadius: "0 5px 5px 0"
                                            }}
                                            disableFormatting={true}
                                            forceDialCode={true}
                                        />
                                    );

                                case "number":
                                    return (
                                        <input
                                            id={customQuestion.id}
                                            type="number"
                                            className=""
                                            name="name"
                                            value={customQuestion.answer}
                                            onChange={(e) => updateAnswer(customQuestion.id, e.target.value)}
                                            required={customQuestion.required === "required"}
                                            aria-invalid={!!errors[customQuestion.id]}
                                        />
                                    );

                                default:
                                    return (
                                        <input
                                            id={customQuestion.id}
                                            className=""
                                            name="name"
                                            value={customQuestion.answer}
                                            onChange={(e) => updateAnswer(customQuestion.id, e.target.value)}
                                            required={customQuestion.required === "required"}
                                            aria-invalid={!!errors[customQuestion.id]}
                                        />
                                    );
                            }
                        })()}
                        <p className="error-message">{errors[customQuestion.id] || ''}</p>

                    </div>
                ))}
                <div className="buttons">
                    <button
                        type="button"
                        onClick={() => onCancel()}
                        style={{ cursor: loading ? "not-allowed" : "pointer" }}
                        className="button secondary"
                        disabled={loading}
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        className="button primary"
                        style={{ cursor: loading ? "not-allowed" : "pointer" }}
                        onClick={() => scheduleEvent()}
                        disabled={loading}
                    >
                        Schedule
                    </button>
                </div>
            </form>
            <style>
                {`
                    .error-message {
                        color: red;
                        font-size: 14px;
                        margin-top: -8px;
                    }
                `}
            </style>
        </div>
    );
}

export default function Content({
    userDetails,
    markedDates,
    timezone,
    updateSelectedTimeAndDate,
    showTimeIn24Hr,
    setShowTimeIn24Hr,
    setMeetingBooked,
    checkIfDateSelected,
    darkModeEnabled,
    onRetry
}) {
    const [date, setDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const timeZone = timezone;
    const smallScreen = window.matchMedia('(max-width: 900px)').matches;

    const validMarkedDates = React.useMemo(() => {
        return (markedDates || []).filter(d => /^\d{2}-\d{2}-\d{4}$/.test(d));
    }, [markedDates]);

    const markedDateBounds = React.useMemo(() => {
        if (validMarkedDates.length === 0) return { min: null, max: null };
        const dates = validMarkedDates
            .map((d) => {
                const [dd, mm, yyyy] = d.split("-");
                return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
            })
            .sort((a, b) => a - b);
        return { min: dates[0], max: dates[dates.length - 1] };
    }, [validMarkedDates]);

    if (selectedTime) {
        return (
            <ScheduleForm
                customFields={userDetails.meeting_custom_questions}
                onCancel={() => {
                    setSelectedTime(null);
                    updateSelectedTimeAndDate({ time: null });
                }}
                selectedTime={selectedTime}
                timeZone={timeZone}
                date={selectedDate}
                setMeetingBooked={setMeetingBooked}
            />
        );
    }

    function isDateEnabled(dateTs) {
        const todayStr = dayjs(dateTs).format("DD-MM-YYYY");
        // markedDates are bookable dates; return false so they stay enabled
        return !validMarkedDates.includes(todayStr);
    }

    function updateDate(date) {
        setDate(date);
        setSelectedDate(date);
        updateSelectedTimeAndDate({ date });
        checkIfDateSelected(true)

    }

    function gotoPreviousMonth() {
        const newDate = new Date(new Date(date.toString()).setMonth(date.getMonth() - 1));
        setDate(newDate);
    }
    function gotoNextMonth() {
        const newDate = new Date(new Date(date.toString()).setMonth(date.getMonth() + 1));
        setDate(newDate);
    }

    return (
        <div className="content">
            {(!smallScreen || !selectedDate) && (

                <div>
                    <div className="controls">
                        <div className="month-year">
                            <div className="month">{MONTHS[dayjs(date).month()]}</div>
                            <div className="year">{dayjs(date).year()}</div>
                        </div>
                        <div className="backward-forward">
                            {(function () {
                                const enabled = markedDateBounds.min
                                    ? (date.getFullYear() > markedDateBounds.min.getFullYear() ||
                                        (date.getFullYear() === markedDateBounds.min.getFullYear() && date.getMonth() > markedDateBounds.min.getMonth()))
                                    : false;

                                return (
                                    <button
                                        className={`backward ${enabled ? "enabled" : ""}`}
                                        onClick={() => {
                                            enabled && gotoPreviousMonth();
                                        }}
                                    >
                                        {
                                            darkModeEnabled ?
                                                <>
                                                    <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M13.75 16.25C13.6515 16.2505 13.5538 16.2313 13.4628 16.1935C13.3718 16.1557 13.2893 16.1001 13.22 16.03L9.72001 12.53C9.57956 12.3894 9.50067 12.1988 9.50067 12C9.50067 11.8013 9.57956 11.6107 9.72001 11.47L13.22 8.00003C13.361 7.90864 13.5285 7.86722 13.6958 7.88241C13.8631 7.89759 14.0205 7.96851 14.1427 8.08379C14.2649 8.19907 14.3448 8.35203 14.3697 8.51817C14.3946 8.68431 14.363 8.85399 14.28 9.00003L11.28 12L14.28 15C14.4205 15.1407 14.4994 15.3313 14.4994 15.53C14.4994 15.7288 14.4205 15.9194 14.28 16.06C14.1353 16.1907 13.9448 16.259 13.75 16.25Z" fill="#ffffff"></path> </g></svg>
                                                </>
                                                :
                                                <>
                                                    <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                        <g id="SVGRepo_iconCarrier">
                                                            <path d="M13.75 16.25C13.6515 16.2505 13.5538 16.2313 13.4628 16.1935C13.3718 16.1557 13.2893 16.1001 13.22 16.03L9.72001 12.53C9.57956 12.3894 9.50067 12.1988 9.50067 12C9.50067 11.8013 9.57956 11.6107 9.72001 11.47L13.22 8.00003C13.361 7.90864 13.5285 7.86722 13.6958 7.88241C13.8631 7.89759 14.0205 7.96851 14.1427 8.08379C14.2649 8.19907 14.3448 8.35203 14.3697 8.51817C14.3946 8.68431 14.363 8.85399 14.28 9.00003L11.28 12L14.28 15C14.4205 15.1407 14.4994 15.3313 14.4994 15.53C14.4994 15.7288 14.4205 15.9194 14.28 16.06C14.1353 16.1907 13.9448 16.259 13.75 16.25Z" fill="#000000"></path>
                                                        </g>
                                                    </svg>

                                                </>
                                        }
                                    </button>
                                );
                            })()}
                            <div className="seprator"></div>

                            {(function () {
                                const enabled = markedDateBounds.max
                                    ? (date.getFullYear() < markedDateBounds.max.getFullYear() ||
                                        (date.getFullYear() === markedDateBounds.max.getFullYear() && date.getMonth() < markedDateBounds.max.getMonth()))
                                    : false;

                                return (
                                    <button
                                        className={`forward ${enabled ? "enabled" : ""}`}
                                        onClick={() => {
                                            enabled && gotoNextMonth();
                                         }}
                                    >
                                        {
                                            darkModeEnabled ?
                                                <>
                                                    <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M10.25 16.25C10.1493 16.2466 10.0503 16.2227 9.95921 16.1797C9.86807 16.1367 9.78668 16.0756 9.72001 16C9.57956 15.8594 9.50067 15.6688 9.50067 15.47C9.50067 15.2713 9.57956 15.0806 9.72001 14.94L12.72 11.94L9.72001 8.94002C9.66069 8.79601 9.64767 8.63711 9.68277 8.48536C9.71786 8.33361 9.79933 8.19656 9.91586 8.09322C10.0324 7.98988 10.1782 7.92538 10.3331 7.90868C10.4879 7.89198 10.6441 7.92391 10.78 8.00002L14.28 11.5C14.4205 11.6407 14.4994 11.8313 14.4994 12.03C14.4994 12.2288 14.4205 12.4194 14.28 12.56L10.78 16C10.7133 16.0756 10.6319 16.1367 10.5408 16.1797C10.4497 16.2227 10.3507 16.2466 10.25 16.25Z" fill="#ffffff"></path> </g></svg>
                                                </>
                                                :
                                                <>
                                                    <svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                                                        <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                                                        <g id="SVGRepo_iconCarrier">
                                                            <path d="M10.25 16.25C10.1493 16.2466 10.0503 16.2227 9.95921 16.1797C9.86807 16.1367 9.78668 16.0756 9.72001 16C9.57956 15.8594 9.50067 15.6688 9.50067 15.47C9.50067 15.2713 9.57956 15.0806 9.72001 14.94L12.72 11.94L9.72001 8.94002C9.66069 8.79601 9.64767 8.63711 9.68277 8.48536C9.71786 8.33361 9.79933 8.19656 9.91586 8.09322C10.0324 7.98988 10.1782 7.92538 10.3331 7.90868C10.4879 7.89198 10.6441 7.92391 10.78 8.00002L14.28 11.5C14.4205 11.6407 14.4994 11.8313 14.4994 12.03C14.4994 12.2288 14.4205 12.4194 14.28 12.56L10.78 16C10.7133 16.0756 10.6319 16.1367 10.5408 16.1797C10.4497 16.2227 10.3507 16.2466 10.25 16.25Z" fill="#000000"></path>
                                                        </g>
                                                    </svg>

                                                </>
                                        }
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                    {validMarkedDates.length === 0 ? (
                        <div className="no-dates-message">
                            <p>No available meeting times for this calendar.</p>
                            {onRetry && (
                                <button onClick={onRetry} type="button">
                                    Retry
                                </button>
                            )}
                        </div>
                    ) : (
                    <Calendar
                        tileDisabled={(today) => isDateEnabled(today.date.getTime())}
                        onChange={updateDate}
                        value={date}
                        tileClassName={({ date, view }) => {
                            const classes = [];
                            const pos = ["", "pos-one", "pos-two", "pos-three", "pos-four", "pos-five", "pos-six"];

                            // if it's the first day of the month then align it accordingly
                            if (date.getDate() === 1) {
                                classes.push("first-date");
                                classes.push(pos[date.getDay()]);
                            }

                            // these are the available dates
                            if (validMarkedDates.find((x) => x === dayjs(date).format("DD-MM-YYYY"))) classes.push("hilighted_date");

                            // these dates are in the future
                            if (date.getMonth() > new Date().getMonth() || date.getDate() > new Date().getDate()) classes.push("future-date");

                            return classes.join(" ");
                        }}
                        showNavigation={false}
                        showNeighboringMonth={false}
                        calendarType="gregory"
                    />
                    )}
                </div>
            )}

            {selectedDate && (
                <TimePicker
                    selectedDate={selectedDate}
                    onSelect={(selected) => {
                        setSelectedTime(selected);
                        updateSelectedTimeAndDate({ time: selected });
                    }}
                    userDetails={userDetails}
                    timeZone={timeZone}
                    showTimeIn24Hr={showTimeIn24Hr}
                    setShowTimeIn24Hr={setShowTimeIn24Hr}
                    selectedTime={selectedTime}
                />
            )}
        </div>
    );
}
